<template>
  <VpVueForm
    :fields="fields"
    :id="id"
    class="vp-p-4"
    :get="get"
    :save="save"
    :del="del"
    width="500px"
    feature="WHATSAPP_BROADCAST"
    cache="whatsappCloudApiBroadcastTemplate"
    collection-cache="whatsappCloudApiBroadcastTemplates"
    ref="templateForm"
  >
    <template #default="{ state, isEditing }">
      <fieldset :disabled="isEditing">
        <VpField rules="required" name="Name" label="Name">
          <VpInput>
            <VpTextBox v-model="state.name" />
          </VpInput>
        </VpField>

        <VpField
          class="vp-mt-6"
          rules="required"
          name="Template"
          label="Template"
        >
          <VpInput>
            <VpSelect
              v-model="state.templateId"
              :options="templates"
              @input="(val) => onTemplateChange(val, state)"
            />
          </VpInput>
        </VpField>

        <div v-if="state.templateType">
          <VpField class="vp-mt-6" name="Type" label="Type">
            <div class="vp-text-sm vp-text-gray-900 vp-capitalize">
              {{ state.templateType }}
            </div>
          </VpField>

          <VpField
            v-model="state.templateType"
            v-if="state.templateType && state.templateType !== 'text'"
            label="Media"
            rules="required"
            class="vp-mt-6"
            name="Media"
          >
            <div>
              <VpImageEditor
                id="BROADCAST_MEDIA"
                v-model="media"
                @delete="onDeleteMedia(state.mediaId)"
                :accept="$options.fileTypes[state.templateType]"
                #default="{ value }"
              >
                <VpMedia size="96" :value="value" />
              </VpImageEditor>
            </div>
          </VpField>

          <VpField class="vp-mt-6" name="Type" label="Language">
            <div class="vp-text-sm vp-text-gray-900">
              {{ state.language.label }}
            </div>
          </VpField>

          <VpField
            label="Variables"
            note="Please select the correct template and assign the variable values carefully, as it cannot be edited in Vepaar once created."
            class="vp-mt-6"
          >
            <div v-if="state.variables && state.variables.length">
              <div
                v-for="(variable, index) in state.variables"
                :key="index"
                class="vp-flex vp-space-x-4"
              >
                <VpField class="vp-flex-1">
                  <VpInput>
                    <VpTextBox
                      :value="variable.name"
                      placeholder="Template Variable"
                      disabled
                    />
                  </VpInput>
                </VpField>
                <VpField
                  class="vp-flex-1"
                  rules="required"
                  :name="`Variable ` + variable.name"
                >
                  <VpInput>
                    <VpSelect
                      v-model="variable.value"
                      :options="variableOptions"
                      placeholder="Select Variable"
                    />
                  </VpInput>
                </VpField>
              </div>
            </div>
            <div v-else class="vp-text-gray-900 vp-text-sm">
              No variables available for this template
            </div>
          </VpField>
        </div>
      </fieldset>
    </template>

    <template #footer="{ isEditing, saveItem, isSaving }">
      <div class="vp-flex vp-justify-between vp-items-center vp-space-x-2">
        <VyButton
          :disabled="isEditing"
          :label="isEditing ? 'Save' : 'Create'"
          :loading="isSaving"
          @click.native="saveItem"
          class="button--primary button--solid button--md button--rounded"
        />
        <VyButton
          v-if="isEditing"
          :loading="deleting"
          label="Delete"
          :icon="$options.icons.Delete"
          @click.native="del"
          class="button--danger button--square button--muted button--md button--rounded"
        />
      </div>
    </template>
  </VpVueForm>
</template>

<script>
import { OpenInNew, Delete } from "icons/icons.js";
import { uploadMediaToS3, deleteMedia } from "utils/presets";
import collection from "./template/collection.gql";
import create from "./create.gql";
import getWhatsappTemplate from "./get.gql";
import getTemplate from "./template/get.gql";
import deleteWhatsappTemplate from "./delete.gql";
import languageCodes from "@/assets/whatsappCloudApiLanguageCodes";

export default {
  icons: { OpenInNew, Delete },
  languageCodes,
  fileTypes: {
    image: ".jpg,.jpeg,.png",
    video: ".3gp,.mp4",
    document: ".pdf,.csv,.doc,.docx,.ppt,.xls,.xlsx,.rtf,.txt",
  },

  props: { id: [Number, String] },

  data() {
    return {
      media: null,
      templates: [],
      deleting: false,
      fields: [
        "name",
        "templateId",
        "templateType",
        "mediaId",
        "variables",
        "templateName",
        "language",
      ],
      variableOptions: [
        { label: "Name", value: "name" },
        { label: "WhatsApp Number", value: "whatsapp_no" },
        { label: "WhatsApp Number (Plain)", value: "whatsapp_no_plain" },
        { label: "City", value: "city" },
        { label: "Address", value: "address" },
        { label: "Email", value: "email" },
        { label: "Country", value: "country" },
        { label: "Timezone", value: "timezone" },
        { label: "Note", value: "note" },
      ],
    };
  },

  mounted() {
    this.getTemplates();
  },

  methods: {
    async getTemplates() {
      try {
        const { data } = await this.$query(collection);
        this.templates =
          data.whatsappCloudApiTemplates.data.map((t) => ({
            value: t.id,
            label: t.name,
          })) || [];
      } catch (error) {
        this.templates = [];
      }
    },

    getLanguage(language) {
      const lang = languageCodes.find((l) => l.value === language);

      return lang;
    },

    get(id) {
      return this.$query(getWhatsappTemplate, { id })
        .then(({ data }) => {
          const whatsappTemplate = data.whatsappCloudApiBroadcastTemplate;
          const lang = this.getLanguage(whatsappTemplate.template.language);

          this.media = whatsappTemplate.media ?? null;
          return {
            values: {
              name: whatsappTemplate.name,
              templateId: whatsappTemplate.template.id,
              templateType: whatsappTemplate.template.format,
              templateName: whatsappTemplate.template.name,
              language: lang,
              mediaId: whatsappTemplate.media?.id ?? null,
              variables:
                whatsappTemplate.variables.map((v) => ({
                  name: v.name,
                  value: v.value,
                })) ?? [],
            },
            res: data,
          };
        })
        .catch(() => {});
    },

    onTemplateChange(templateId, state) {
      if (!templateId) {
        return;
      }

      this.$query(getTemplate, { templateId })
        .then(({ data }) => {
          const template = data.whatsappCloudApiTemplate;

          const lang = this.getLanguage(template.language);

          state.templateId = template.id;
          state.templateName = template.name;
          state.language = lang;
          state.templateType = template.format;
          state.variables = (template.variables || []).map((v) => ({
            name: v.name,
            value: v.value,
          }));
        })
        .catch(() => {});
    },

    async save(id, data) {
      if (this.media?.status == "CHANGE") {
        data.mediaId = await this.upload();
      }

      this.$mutate(create, {
        input: {
          name: data.name,
          template: {
            id: data.templateId,
            name: data.templateName,
            language: data.language.value,
            format: data.templateType,
            mediaId: data.mediaId,
            variables: data.variables,
          },
        },
      }).then(() => {
        this.$router.push({ name: "whatsapp-templates" });
      });
    },

    del() {
      this.$vayu.confirm({
        name: "delete",
        title: "Delete Template?",
        message:
          "Please unassign or delete the broadcast linked to the template before deleting it?",
        ok: async ({ close }) => {
          close();
          this.deleting = true;

          try {
            await this.$mutate(deleteWhatsappTemplate, { id: this.id });

            this.$cache.evict({
              id: "ROOT_QUERY",
              fieldName: "whatsappCloudApiBroadcastTemplates",
            });

            this.deleting = false;

            this.$vayu.notify({
              title: "Template deleted successfully!",
              state: "success",
            });

            this.$router.push({ name: "whatsapp-templates" });
          } catch (err) {
            this.$vayu.notify({
              title: err.message,
              state: "danger",
            });
            this.deleting = false;
          }
        },
        cancel: ({ close }) => {
          close();
        },
      });
    },

    async upload() {
      if (!this.media) return null;
      try {
        return (
          await uploadMediaToS3({
            path: "campaign_broadcast_template",
            file: this.media,
          })
        ).id;
      } catch {
        return null;
      }
    },

    async onDeleteMedia(id) {
      if (id) await deleteMedia([id]);
      this.media = null;
    },
  },
};
</script>
